import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { SplitText, RotatingText } from "../components/motion";
import { WorldDesk } from "../components/WorldDesk";
import { Mixtape } from "../components/Mixtape";

const BRANDS_1 = ["UNICEF", "HDFC Bank", "Abu Dhabi Mobility", "HUFT", "Bhutani", "Glen Grant", "SKYY Vodka", "HelpAge India"];
const BRANDS_2 = ["Sharda", "Alcobrew", "Chaos Global", "Magic Jewels", "Civitas", "Moonstone", "LadyTin", "Policybazaar"];

const FOLDERS2 = [
  { id: "brand", to: "/work?c=brand", c: "#1E22FF", ink: "#fff", n: "05", h: "Brand identity", p: "Flow Academy · Snowball · Logofolio", posters: ["flow-academy", "snowball", "logofolio"] },
  { id: "campaign", to: "/work?c=campaign", c: "#FF4D2E", ink: "#fff", n: "06", h: "Campaigns & KVs", p: "GCC Traffic Week 2026 · HelpAge India · Not Your Regular Glen", posters: ["gcc-traffic-week", "helpage-india", "not-your-regular-glen"] },
  { id: "packaging", to: "/work?c=packaging", c: "#D7FF3B", ink: "#0D0D12", n: "03", h: "Packaging", p: "Farkmin · Quips · SKYY Vodka", posters: ["farkmin", "quips", "skyy-vodka"] },
  { id: "social", to: "/work?c=social", c: "#FF6FB5", ink: "#0D0D12", n: "14", h: "Social & digital", p: "NutriMeow × HUFT · HDFC Pixel · UNICEF ESARO", posters: ["meowsi-huft", "hdfc-pixel", "unicef-esaro"] },
  { id: "decks", to: "/work?c=decks", c: "#0D0D12", ink: "#fff", n: "07", h: "Print, decks & editorial", p: "The Regatta · Thea Pâtisserie · Kairo & Swastik", posters: ["the-regatta", "thea-patisserie", "pitch-decks"] },
  { id: "motion", to: "/work?c=motion", c: "#7B61FF", ink: "#fff", n: "03", h: "Motion & AI film", p: "Emara · DIP documentary · RPS Group", posters: ["emara-launch-films", "dip-documentary", "rps-navratri"] },
  { id: "product", to: "/work?c=product", c: "#00B37E", ink: "#fff", n: "01", h: "Product", p: "CHAOS Creative Command Center", posters: ["chaos-command-center"] },
  { id: "playground", to: "/playground", c: "#FFB800", ink: "#0D0D12", n: "13", h: "Playground", p: "Alphabet cards · This is my Home · Silence", posters: ["alphabet-cards", "this-is-my-home", "silence"] },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Helmet><title>Poonam Baid — Lead Visual Designer | vdesigns.eps</title></Helmet>

      {/* 1 · HERO */}
      <section className="page-x pt-28 md:pt-32 pb-10 hero">
        <div className="wrap">
          <h1 style={{ margin: 0 }} data-testid="hero-wordmark">
            <span className="disp name"><SplitText text="Poonam" /></span>
            <span className="disp name"><SplitText text="Baid" delay={0.22} /></span>
          </h1>
          <div className="row">
            <p>
              Lead visual designer —{" "}
              <RotatingText className="serif" style={{ color: "var(--blue)" }} words={["brand", "campaigns", "packaging", "decks", "AI film"]} />
              . I turn <span className="serif" style={{ color: "var(--blue)" }}>open briefs</span> into visual systems — identities, campaigns, packaging, editorials and decks.
            </p>
            <div className="mono" style={{ color: "var(--grey)" }}>(A) multidisciplinary visual designer<br /><br />Scroll to open the file ↓</div>
            <div className="meta">
              <div><span>Based</span>Gurugram, IN</div>
              <div><span>Experience</span>5+ yrs full-time</div>
              <div><span>Status</span><i className="dot" />Open to projects</div>
            </div>
          </div>
        </div>
      </section>

      {/* 1 · BRANDS STRIP */}
      <section className="clients" id="brands" data-testid="brands-strip">
        <div className="b-head wrap"><span className="mono">(01) Brands I've designed for</span><span className="mono">16+ brands · agency & in-house</span></div>
        <div className="rw r1"><div className="t t1">{[...BRANDS_1, ...BRANDS_1, ...BRANDS_1].map((b, i) => <span key={i}>{b}</span>)}</div></div>
        <div className="rw r2"><div className="t t2">{[...BRANDS_2, ...BRANDS_2, ...BRANDS_2].map((b, i) => <span key={i}>{b}</span>)}</div></div>
      </section>

      {/* 2 · INTERACTIVE SHOWREEL */}
      <section className="reel2" id="reel" data-testid="showreel-band">
        <div className="wrap rhead">
          <span className="mono">(02) The open file — interactive showreel</span>
          <span className="mono">Click a chapter · ←/→ to skip · hover to pause · click any work to open its file</span>
        </div>
        <div className="rframe">
          <iframe src="/showreel/interactive/index.html?play&base=/work/&ext=" title="Poonam Baid — interactive showreel" loading="lazy" allow="autoplay" data-testid="showreel-iframe" />
        </div>
        <div className="wrap rfoot mono">
          <span>SHOWREEL_2026.EPS · 60s · silent</span>
          <a href="/showreel/showreel-2026-v2.mp4" target="_blank" rel="noopener">Watch as a video ↗</a>
        </div>
      </section>

      {/* 3 · OPEN A FOLDER */}
      <section className="page-x py-16">
        <div className="wrap">
          <div className="sh"><span className="mono">(03) Open a folder</span><span className="mono">Every file, sorted by what it is</span></div>
          <div className="folders2">
            {FOLDERS2.map((f) => (
              <Link key={f.id} to={f.to} className="fold2" style={{ "--c": f.c, "--ink": f.ink }} data-testid={`folder-${f.id}`}>
                <div className="f-back" />
                <div className="f-papers">
                  {f.posters.map((slug, k) => (
                    <img key={slug} src={`/covers/${slug}/still.jpg`} alt="" loading="lazy" style={{ "--k": k }} />
                  ))}
                </div>
                <div className="f-front"><span className="mono">/{f.id}</span><b>{f.n}</b><h4>{f.h}</h4><p>{f.p}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · WHAT I DO */}
      <section className="page-x py-16 stmt">
        <div className="wrap">
          <div className="sh"><span className="mono">(04) What I do</span></div>
          <h2>
            From the first <span className="serif">pitch deck</span> to the last hoarding — I build <span className="hl">visual systems</span> that hold together across identity, packaging, campaigns and <span className="serif">every screen</span> in between.
          </h2>
        </div>
      </section>

      {/* 5 · OPEN THE DESK */}
      <WorldDesk ticketHref="#reel" />

      {/* 6 · MIXTAPE */}
      <Mixtape />
    </div>
  );
}
